import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Input, Label } from '@theraply/ui';
import { TherapistSearchFilters, useSpecialties } from '../../hooks/useTherapistSearch';

interface SearchFiltersProps {
  filters: TherapistSearchFilters;
  onFiltersChange: (filters: Partial<TherapistSearchFilters>) => void;
  onClearFilters: () => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

/**
 * Search filters sidebar component.
 *
 * Features:
 * - Specialty multi-select with search
 * - Session type checkboxes
 * - Rate range slider with inputs
 * - Availability day/time pickers
 * - Clear all filters button
 * - Active filter count badge
 *
 * Performance:
 * - Debounced inputs handled by parent hook
 * - Memoized to prevent unnecessary re-renders
 */
export const SearchFilters = React.memo<SearchFiltersProps>(({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const { data: availableSpecialties = [], isLoading: specialtiesLoading } = useSpecialties();
  const [specialtySearch, setSpecialtySearch] = useState('');

  // Local state for rate range
  const [minRate, setMinRate] = useState(filters.minRate?.toString() || '');
  const [maxRate, setMaxRate] = useState(filters.maxRate?.toString() || '');

  // Count active filters
  const activeFilterCount = [
    filters.specialty?.length,
    filters.sessionType,
    filters.minRate,
    filters.maxRate,
    filters.dayOfWeek,
  ].filter(Boolean).length;

  // Filtered specialties based on search
  const filteredSpecialties = availableSpecialties.filter((specialty) =>
    specialty.toLowerCase().includes(specialtySearch.toLowerCase())
  );

  // Handle specialty selection
  const handleSpecialtyToggle = (specialty: string) => {
    const currentSpecialties = filters.specialty || [];
    const newSpecialties = currentSpecialties.includes(specialty)
      ? currentSpecialties.filter((s) => s !== specialty)
      : [...currentSpecialties, specialty];

    onFiltersChange({
      specialty: newSpecialties.length > 0 ? newSpecialties : undefined,
    });
  };

  // Handle session type
  const handleSessionTypeChange = (type: 'in_person' | 'video' | 'both') => {
    onFiltersChange({
      sessionType: filters.sessionType === type ? undefined : type,
    });
  };

  // Handle rate range
  const handleMinRateChange = (value: string) => {
    setMinRate(value);
    const numValue = parseFloat(value);
    onFiltersChange({
      minRate: value && !isNaN(numValue) ? numValue : undefined,
    });
  };

  const handleMaxRateChange = (value: string) => {
    setMaxRate(value);
    const numValue = parseFloat(value);
    onFiltersChange({
      maxRate: value && !isNaN(numValue) ? numValue : undefined,
    });
  };

  // Handle availability
  const handleDayChange = (day: number) => {
    onFiltersChange({
      dayOfWeek: filters.dayOfWeek === day ? undefined : day,
    });
  };

  const handleStartTimeChange = (time: string) => {
    onFiltersChange({ startTime: time || undefined });
  };

  const handleEndTimeChange = (time: string) => {
    onFiltersChange({ endTime: time || undefined });
  };

  // Format specialty label
  const formatSpecialty = (specialty: string) => {
    return specialty
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-600 text-xs text-white">
              {activeFilterCount}
            </span>
          )}
        </h2>
        {activeFilterCount > 0 && (
          <button
            onClick={onClearFilters}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Specialties */}
      <div className="mb-6">
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Specialties
        </Label>

        {/* Search specialties */}
        <Input
          type="text"
          placeholder="Search specialties..."
          value={specialtySearch}
          onChange={(e) => setSpecialtySearch(e.target.value)}
          className="mb-3"
        />

        {/* Specialty list */}
        <div className="max-h-64 overflow-y-auto space-y-2">
          {specialtiesLoading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : filteredSpecialties.length > 0 ? (
            filteredSpecialties.map((specialty) => (
              <label
                key={specialty}
                className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
              >
                <input
                  type="checkbox"
                  checked={filters.specialty?.includes(specialty) || false}
                  onChange={() => handleSpecialtyToggle(specialty)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {formatSpecialty(specialty)}
                </span>
              </label>
            ))
          ) : (
            <div className="text-sm text-gray-500">No specialties found</div>
          )}
        </div>
      </div>

      {/* Session Type */}
      <div className="mb-6">
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Session Type
        </Label>
        <div className="space-y-2">
          <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
            <input
              type="radio"
              name="sessionType"
              checked={filters.sessionType === 'video'}
              onChange={() => handleSessionTypeChange('video')}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Video only</span>
          </label>
          <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
            <input
              type="radio"
              name="sessionType"
              checked={filters.sessionType === 'in_person'}
              onChange={() => handleSessionTypeChange('in_person')}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">In-person only</span>
          </label>
          <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
            <input
              type="radio"
              name="sessionType"
              checked={filters.sessionType === 'both'}
              onChange={() => handleSessionTypeChange('both')}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Both video and in-person</span>
          </label>
        </div>
      </div>

      {/* Rate Range */}
      <div className="mb-6">
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Rate per Session
        </Label>
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <Input
              type="number"
              placeholder="Min"
              value={minRate}
              onChange={(e) => handleMinRateChange(e.target.value)}
              min="0"
              step="10"
            />
          </div>
          <span className="text-gray-500">to</span>
          <div className="flex-1">
            <Input
              type="number"
              placeholder="Max"
              value={maxRate}
              onChange={(e) => handleMaxRateChange(e.target.value)}
              min="0"
              step="10"
            />
          </div>
        </div>
        {(filters.minRate || filters.maxRate) && (
          <div className="mt-2 text-sm text-gray-600">
            ${filters.minRate || 0} - ${filters.maxRate || '∞'}
          </div>
        )}
      </div>

      {/* Availability */}
      <div className="mb-6">
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Availability
        </Label>

        {/* Day of week */}
        <div className="mb-3">
          <select
            value={filters.dayOfWeek?.toString() || ''}
            onChange={(e) =>
              handleDayChange(e.target.value ? parseInt(e.target.value) : undefined as any)
            }
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">Any day</option>
            {DAYS_OF_WEEK.map((day) => (
              <option key={day.value} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
        </div>

        {/* Time range */}
        {filters.dayOfWeek !== undefined && (
          <div className="space-y-2">
            <div>
              <Label className="block text-xs text-gray-600 mb-1">Start time</Label>
              <Input
                type="time"
                value={filters.startTime || ''}
                onChange={(e) => handleStartTimeChange(e.target.value)}
              />
            </div>
            <div>
              <Label className="block text-xs text-gray-600 mb-1">End time</Label>
              <Input
                type="time"
                value={filters.endTime || ''}
                onChange={(e) => handleEndTimeChange(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Sort By */}
      <div className="mb-6">
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Sort By
        </Label>
        <select
          value={filters.sortBy || 'distance'}
          onChange={(e) =>
            onFiltersChange({
              sortBy: e.target.value as 'distance' | 'rating' | 'rate',
            })
          }
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="distance">Distance</option>
          <option value="rating">Rating</option>
          <option value="rate">Rate (Low to High)</option>
        </select>
      </div>
    </div>
  );
});

SearchFilters.displayName = 'SearchFilters';
